<template>
  <v-menu
    v-model="menu"
    ref="menu"
    :close-on-content-click="false"
    offset-y
    left
    nudge-bottom="4"
    :nudge-left="nudgeLeft"
    transition="scale-transition"
    content-class="premium-date-range-menu"
    :min-width="activePreset === 'custom' ? 520 : 180"
    :max-width="activePreset === 'custom' ? 520 : 180"
  >
    <template v-slot:activator="{ on, attrs }">
      <div class="ism-date-picker-trigger" v-bind="attrs" v-on="on" ref="trigger">
        <v-icon size="16" color="var(--text-secondary)">mdi-calendar-range</v-icon>
        <span class="trigger-label">{{ displayValue }}</span>
        <v-icon size="14" class="ml-1" color="var(--text-secondary)">mdi-chevron-down</v-icon>
      </div>
    </template>

    <div class="ism-picker-container" :class="{ 'has-calendar': activePreset === 'custom' }">
      <!-- Left Sidebar: Presets -->
      <div class="ism-picker-sidebar">
        <div
          v-for="p in presets"
          :key="p.id"
          class="preset-item"
          :class="{ active: activePreset === p.id }"
          @click="selectPreset(p)"
        >
          <span>{{ p.label }}</span>
          <v-icon v-if="activePreset === p.id" size="13" color="#F26522" class="ml-auto">mdi-check</v-icon>
        </div>
      </div>

      <!-- Right Side: Calendar (Only if custom) -->
      <transition name="slide-fade">
        <div v-if="activePreset === 'custom'" class="ism-picker-main">
          <div class="picker-header">
            <v-icon size="14" color="#F26522" class="mr-1">mdi-calendar-range</v-icon>
            <span class="current-range">{{ formattedRange }}</span>
          </div>
          <v-date-picker
            v-model="dates"
            range
            no-title
            scrollable
            color="#F26522"
            :picker-date.sync="pickerDate"
            class="custom-v-date-picker"
            width="338"
          ></v-date-picker>
          <div class="picker-actions">
            <button class="btn-cancel" @click="cancel">Cancel</button>
            <button class="btn-apply" @click="apply">Apply</button>
          </div>
        </div>
      </transition>
    </div>
  </v-menu>
</template>

<script>
import moment from 'moment';

export default {
  name: 'PremiumDateRangePicker',

  props: {
    value: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      menu: false,
      dates: this.value.length ? [...this.value] : [],
      pickerDate: null,
      activePreset: 'last_30',
      nudgeLeft: 0,
      presets: [
        { id: 'today',      label: 'Today',        getValue: () => [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')] },
        { id: 'yesterday',  label: 'Yesterday',    getValue: () => [moment().subtract(1, 'day').format('YYYY-MM-DD'), moment().subtract(1, 'day').format('YYYY-MM-DD')] },
        { id: 'last_7',     label: 'Last 7 Days',  getValue: () => [moment().subtract(6, 'days').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')] },
        { id: 'last_30',    label: 'Last 30 Days', getValue: () => [moment().subtract(29, 'days').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')] },
        { id: 'this_month', label: 'This Month',   getValue: () => [moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')] },
        { id: 'last_month', label: 'Last Month',   getValue: () => [moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')] },
        { id: 'custom',     label: 'Custom Range', getValue: () => this.dates },
      ]
    };
  },

  computed: {
    displayValue() {
      if (!this.dates.length) return 'Select Date Range';
      const sorted = [...this.dates].sort();
      if (sorted.length === 1) return moment(sorted[0]).format('MMM DD, YYYY');
      return `${moment(sorted[0]).format('MMM DD')} – ${moment(sorted[1]).format('MMM DD, YYYY')}`;
    },
    formattedRange() {
      if (!this.dates.length) return 'No range selected';
      const sorted = [...this.dates].sort();
      if (sorted.length === 1) return moment(sorted[0]).format('DD/MM/YYYY');
      return `${moment(sorted[0]).format('DD/MM/YYYY')} – ${moment(sorted[1]).format('DD/MM/YYYY')}`;
    }
  },

  methods: {
    selectPreset(p) {
      this.activePreset = p.id;
      if (p.id !== 'custom') {
        this.dates = p.getValue();
        this.apply();
      } else {
        // recalculate nudge when switching to custom (wider panel)
        this.$nextTick(this.calcNudge);
      }
    },

    cancel() {
      this.dates = this.value.length ? [...this.value] : [];
      this.menu = false;
    },

    apply() {
      let finalDates = [...this.dates].sort();
      if (finalDates.length === 1) finalDates = [finalDates[0], finalDates[0]];
      this.$emit('input', finalDates);
      this.$emit('change', finalDates);
      this.menu = false;
    },

    calcNudge() {
      // push menu left so it doesn't overflow the right edge of viewport
      if (!this.$refs.trigger) return;
      const rect = this.$refs.trigger.getBoundingClientRect();
      const menuWidth = this.activePreset === 'custom' ? 520 : 180;
      
      // Account for the menu width when checking for right-edge overflow
      // Since 'left' prop is used, the menu's right edge is aligned with trigger's right edge
      const overflow = rect.right - window.innerWidth + 12;
      this.nudgeLeft = overflow > 0 ? overflow : 0;

      // Also ensure we don't push it off the left edge
      if (rect.right - menuWidth - this.nudgeLeft < 12) {
        const leftOverflow = 12 - (rect.right - menuWidth - this.nudgeLeft);
        this.nudgeLeft -= leftOverflow;
      }
    }
  },

  watch: {
    value(val) {
      this.dates = [...val];
    },
    menu(val) {
      if (val) this.$nextTick(this.calcNudge);
    },
    activePreset() {
      this.$nextTick(() => {
        this.calcNudge();
        // Force Vuetify menu to recalculate its position and dimensions
        // when switching between preset list (180px) and custom calendar (520px)
        setTimeout(() => {
          if (this.$refs.menu && this.$refs.menu.updateDimensions) {
            this.$refs.menu.updateDimensions();
          }
        }, 0);
      });
    }
  }
};
</script>

<style scoped>
/* ── Trigger ── */
.ism-date-picker-trigger {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  padding: 0 12px;
  border-radius: 10px;
  cursor: pointer;
  height: 38px;
  transition: border-color 0.18s, background 0.18s;
  user-select: none;
  min-width: 0;
  max-width: 220px;
}
.ism-date-picker-trigger:hover {
  border-color: #F26522;
  background: var(--bg-secondary);
}

.trigger-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Container ── */
.ism-picker-container {
  display: flex;
  background: var(--card-bg, #fff);
  border-radius: 12px;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
}
.ism-picker-container.has-calendar {
  width: 520px;
}

/* ── Sidebar ── */
.ism-picker-sidebar {
  width: 180px;
  min-width: 180px;
  background: var(--bg-primary, #f8fafc);
  border-right: 1px solid var(--border-color, rgba(0,0,0,0.08));
  display: flex;
  flex-direction: column;
  padding: 6px 0;
}

.preset-item {
  display: flex;
  align-items: center;
  padding: 9px 14px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text-secondary, #64748b);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  gap: 6px;
}
.preset-item:hover {
  background: rgba(242, 101, 34, 0.06);
  color: #F26522;
}
.preset-item.active {
  background: rgba(242, 101, 34, 0.10);
  color: #F26522;
  font-weight: 700;
}

/* ── Main Calendar Panel ── */
.ism-picker-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--card-bg, #fff);
  min-width: 0;
}

.picker-header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border-color, rgba(0,0,0,0.08));
}
.current-range {
  font-size: 13px;
  font-weight: 700;
  color: #F26522;
}

/* ── Force the Vuetify date picker to fill available width ── */
.custom-v-date-picker {
  box-shadow: none !important;
  width: 100% !important;
}
.custom-v-date-picker >>> .v-picker__body {
  background: transparent !important;
  width: 100% !important;
}
.custom-v-date-picker >>> .v-date-picker-table {
  height: 230px !important;
}
.custom-v-date-picker >>> .v-date-picker-table table {
  width: 100%;
  table-layout: fixed;
}
.custom-v-date-picker >>> .v-btn {
  font-family: 'Inter', sans-serif !important;
  font-weight: 600 !important;
  border-radius: 4px !important;
}
.custom-v-date-picker >>> .v-btn--rounded {
  border-radius: 4px !important;
}
.custom-v-date-picker >>> .v-btn:before {
  border-radius: 4px !important;
}
.custom-v-date-picker >>> .v-btn__content {
  border-radius: 4px !important;
}
.custom-v-date-picker >>> .v-date-picker-table .v-btn.v-btn--active {
  border-radius: 4px !important;
}
.custom-v-date-picker >>> .v-date-picker-table__current {
  border-radius: 4px !important;
  border-color: #F26522 !important;
}
.custom-v-date-picker >>> .v-date-picker-header {
  padding: 4px 12px !important;
}

/* ── Actions ── */
.picker-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid var(--border-color, rgba(0,0,0,0.08));
}

.btn-cancel {
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary, #64748b);
  border-radius: 6px;
  background: transparent;
  border: 1px solid var(--border-color, rgba(0,0,0,0.1));
  cursor: pointer;
  transition: background 0.15s;
  font-family: 'Inter', sans-serif;
}
.btn-cancel:hover {
  background: var(--bg-primary, #f1f5f9);
  color: var(--text-primary);
}

.btn-apply {
  padding: 5px 16px;
  font-size: 12px;
  font-weight: 700;
  background: #F26522;
  color: #fff;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: filter 0.15s, transform 0.15s;
  font-family: 'Inter', sans-serif;
}
.btn-apply:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

/* ── Transition ── */
.slide-fade-enter-active { transition: all 0.15s ease; }
.slide-fade-leave-active { transition: all 0.1s ease; }
.slide-fade-enter, .slide-fade-leave-to { opacity: 0; transform: translateX(-6px); }

/* ── Menu wrapper ── */
.premium-date-range-menu {
  border-radius: 12px !important;
  border: 1px solid var(--border-color, rgba(0,0,0,0.1)) !important;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12) !important;
  overflow: visible !important;
}

/* ── Dark mode ── */
.dark-mode .ism-picker-sidebar {
  background: #1e293b;
}
.dark-mode .preset-item:hover {
  background: rgba(242, 101, 34, 0.12);
}
</style>